import { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveContact, parseVendorCodes, MOCK_CONTACTS } from "./contact-mock";

type InfoTab = "general" | "vendors" | "roles";

// Splitter altinda acilan kullanici (contact) bilgi karti.
// Fores "Vendor Management > Users" ekranindaki General / Vendors / Roles sekmeleriyle eslesir.
export function ContactInfoPane({ contactRef }: { contactRef: string }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState<InfoTab>("general");
    const contact = resolveContact(contactRef, MOCK_CONTACTS);

    if (!contact) {
        return (
            <div className="flex items-center justify-center h-full w-full" style={{ color: "var(--subtitle-text-color)" }}>
                {t("contactNotFound")}
            </div>
        );
    }

    const initials = `${contact.firstName?.[0] ?? ""}${contact.lastName?.[0] ?? ""}`.toUpperCase() || contact.userName.slice(0, 2).toUpperCase();
    const vendorCodes = parseVendorCodes(contact.vendors);

    const tabs: { id: InfoTab; label: string; count?: number }[] = [
        { id: "general", label: t("general") },
        { id: "vendors", label: t("vendors"), count: vendorCodes.length },
        { id: "roles", label: t("roles"), count: contact.roles.length },
    ];

    return (
        <div className="h-full w-full flex flex-col" style={{ color: "var(--base-text-color)" }}>
            {/* Baslik */}
            <div className="flex items-center gap-3 shrink-0" style={{ padding: "16px 16px 12px" }}>
                <div
                    className="flex items-center justify-center shrink-0"
                    style={{ width: 48, height: 48, borderRadius: 10, background: "var(--accent-color, #6366F1)", color: "#fff", fontWeight: 600, fontSize: 16 }}
                >
                    {initials}
                </div>
                <div className="min-w-0">
                    <div className="font-semibold truncate" style={{ fontSize: 16 }}>{contact.firstName} {contact.lastName}</div>
                    <div className="text-xs truncate" style={{ color: "var(--subtitle-text-color)" }}>
                        {contact.userName}
                        {contact.isActive === false
                            ? <span style={{ color: "#EF4444", marginLeft: 8 }}>• {t("passive")}</span>
                            : <span style={{ color: "#10B981", marginLeft: 8 }}>• {t("active")}</span>}
                    </div>
                </div>
            </div>

            {/* Sekme cubugu */}
            <div className="flex items-center gap-1 shrink-0" style={{ padding: "0 16px", borderBottom: "1px solid var(--border-color)" }}>
                {tabs.map(tb => (
                    <button
                        key={tb.id}
                        type="button"
                        onClick={() => setTab(tb.id)}
                        className="text-sm"
                        style={{
                            padding: "8px 12px",
                            fontWeight: tab === tb.id ? 600 : 500,
                            color: tab === tb.id ? "var(--base-text-color)" : "var(--subtitle-text-color)",
                            borderBottom: tab === tb.id ? "2px solid var(--accent-color, #6366F1)" : "2px solid transparent",
                            marginBottom: -1,
                        }}
                    >
                        {tb.label}
                        {tb.count != null && tb.count > 0 && (
                            <span className="text-xs" style={{ marginLeft: 6, background: "var(--base-bg-darken-4)", color: "var(--base-text-color)", padding: "1px 6px", borderRadius: 999 }}>
                                {tb.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Sekme icerigi */}
            <div className="flex-1 min-h-0" style={{ overflowY: "auto", padding: 16 }}>
                {tab === "general" && (
                    <Section title={t("userInformation")} emptyText={t("notDefined")} rows={[
                        { label: t("userType"), value: contact.userType },
                        { label: t("userName"), value: contact.userName },
                        { label: t("firstName"), value: contact.firstName },
                        { label: t("lastName"), value: contact.lastName },
                        { label: t("email"), value: contact.email },
                        { label: t("comment"), value: contact.comment },
                    ]} />
                )}

                {tab === "vendors" && (
                    <BadgeList items={vendorCodes} emptyText={t("notDefined")} mono />
                )}

                {tab === "roles" && (
                    <BadgeList items={contact.roles} emptyText={t("notDefined")} />
                )}
            </div>
        </div>
    );
}

function BadgeList({ items, emptyText, mono }: { items: string[]; emptyText: string; mono?: boolean }) {
    if (items.length === 0) {
        return (
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, padding: "12px 14px" }}>
                <span className="text-xs" style={{ color: "var(--subtitle-text-color)", fontStyle: "italic" }}>{emptyText}</span>
            </div>
        );
    }
    return (
        <div className="flex flex-wrap gap-2" style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, padding: "12px 14px" }}>
            {items.map(item => (
                <span
                    key={item}
                    className={`text-xs${mono ? " font-mono" : ""}`}
                    style={{ background: "var(--base-bg-darken-4)", color: "var(--base-text-color)", padding: "3px 9px", borderRadius: 6 }}
                >
                    {item}
                </span>
            ))}
        </div>
    );
}

function Section({ title, rows, emptyText }: { title: string; rows: { label: string; value?: string }[]; emptyText: string }) {
    const filled = rows.filter(r => r.value != null && r.value !== "");
    return (
        <div style={{ marginBottom: 16 }}>
            <div className="text-xs font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 6 }}>
                {title}
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                {filled.length === 0 ? (
                    <div className="text-xs" style={{ padding: "12px 14px", color: "var(--subtitle-text-color)", fontStyle: "italic" }}>
                        {emptyText}
                    </div>
                ) : filled.map((r, i) => (
                    <div key={r.label}
                        className="flex items-center justify-between gap-3"
                        style={{ padding: "10px 14px", borderBottom: i === filled.length - 1 ? "none" : "1px solid var(--border-color)" }}
                    >
                        <span className="text-xs shrink-0" style={{ color: "var(--subtitle-text-color)" }}>{r.label}</span>
                        <span className="text-sm truncate" style={{ textAlign: "right" }}>{r.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
